// TestFlightScreen.js — App Store Connect beta tester management

import React, { useState, useEffect, useMemo, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import {
  getTestFlightTesters,
  getTestFlightGroups,
  inviteTestFlightTester,
  resendTestFlightInvite,
  deleteTestFlightTester,
} from "./adminService";
import "./TestFlightScreen.css";

const FILTERS = [
  { id: "all",       label: "All",       icon: "bi-grid" },
  { id: "invited",   label: "Invited",   icon: "bi-envelope" },
  { id: "accepted",  label: "Accepted",  icon: "bi-person-check" },
  { id: "installed", label: "Installed", icon: "bi-phone-vibrate" },
];

const STATE_META = {
  INVITED:   { id: "invited",   label: "Invited",   cls: "dt-pill--warn" },
  ACCEPTED:  { id: "accepted",  label: "Accepted",  cls: "dt-pill--info" },
  INSTALLED: { id: "installed", label: "Installed", cls: "dt-pill--success" },
};

const stateMeta = (state) =>
  STATE_META[state] || { id: "other", label: state || "Unknown", cls: "dt-pill--muted" };

const avatarColor = (seed) => {
  const palette = ["dt-avatar--teal", "dt-avatar--violet", "dt-avatar--blue", ""];
  return palette[(seed || "").charCodeAt(0) % palette.length || 0];
};

const initialsOf = (t) => {
  const f = (t.firstName || "").trim();
  const l = (t.lastName || "").trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "?";
  return (t.email || "?").slice(0, 2).toUpperCase();
};

const apiErrorMessage = (err, fallback) =>
  err?.response?.data?.error || err?.message || fallback;

const formatTimestamp = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return diffSec < 0 ? "in moments" : "just now";
  const mins = Math.round(diffSec / 60);
  if (Math.abs(mins) < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (Math.abs(hrs) < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (Math.abs(days) < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (Math.abs(months) < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
};

function TestFlightScreen({ globalSearch = "" }) {
  const [testers, setTesters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refresh, setRefresh] = useState(false);

  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    groupId: "",
  });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const [confirmRemove, setConfirmRemove] = useState(null); // tester obj or null
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  const [confirmResend, setConfirmResend] = useState(null); // tester obj or null
  const [resendSubmitting, setResendSubmitting] = useState(false);

  const [rowBusy, setRowBusy] = useState({}); // { [testerId]: "resending" | "removing" }

  const token = () => localStorage.getItem("@uphealth_login_token");

  const loadTesters = useCallback(async (search) => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await getTestFlightTesters(token(), search);
      setTesters(data?.data || []);
    } catch (err) {
      setTesters([]);
      setLoadError(
        apiErrorMessage(err, "Could not load testers from App Store Connect")
      );
    }
    setLoading(false);
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const { data } = await getTestFlightGroups(token());
      setGroups(data?.data || []);
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const effective = debouncedSearch || globalSearch || "";
    loadTesters(effective);
  }, [debouncedSearch, globalSearch, refresh, loadTesters]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const counts = useMemo(() => {
    const c = { all: testers.length, invited: 0, accepted: 0, installed: 0 };
    testers.forEach((t) => {
      const id = stateMeta(t.state).id;
      if (c[id] !== undefined) c[id]++;
    });
    return c;
  }, [testers]);

  const filteredTesters = useMemo(() => {
    if (filter === "all") return testers;
    return testers.filter((t) => stateMeta(t.state).id === filter);
  }, [testers, filter]);

  const openInviteModal = () => {
    setInviteForm({
      firstName: "",
      lastName: "",
      email: "",
      groupId: groups[0]?.id || "",
    });
    setInviteError("");
    setShowInvite(true);
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviteError("");

    const email = inviteForm.email.trim();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailValid) {
      setInviteError("Enter a valid email address.");
      return;
    }
    if (!inviteForm.groupId) {
      setInviteError("Pick a TestFlight beta group.");
      return;
    }

    setInviteSubmitting(true);
    try {
      await inviteTestFlightTester(token(), {
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim(),
        email,
        groupId: inviteForm.groupId,
      });
      setShowInvite(false);
      setRefresh((r) => !r);
    } catch (err) {
      setInviteError(
        apiErrorMessage(err, "Apple rejected the invitation request.")
      );
    }
    setInviteSubmitting(false);
  };

  const handleConfirmResend = async () => {
    if (!confirmResend) return;
    const tester = confirmResend;
    setResendSubmitting(true);
    setRowBusy((m) => ({ ...m, [tester.id]: "resending" }));
    try {
      await resendTestFlightInvite(token(), tester.id);
      setConfirmResend(null);
      setRefresh((r) => !r);
    } catch (err) {
      alert(apiErrorMessage(err, "Could not resend invitation."));
    }
    setRowBusy((m) => {
      const next = { ...m };
      delete next[tester.id];
      return next;
    });
    setResendSubmitting(false);
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemove) return;
    const tester = confirmRemove;
    setRemoveSubmitting(true);
    try {
      await deleteTestFlightTester(token(), tester.id);
      setConfirmRemove(null);
      setRefresh((r) => !r);
    } catch (err) {
      alert(apiErrorMessage(err, "Could not remove tester."));
    }
    setRemoveSubmitting(false);
  };

  return (
    <>
      <div className="tf-container">
        {/* ── Page Header ── */}
        <div className="page-header">
          <div>
            <h1 className="page-header__title">TestFlight Testers</h1>
            <div className="page-header__subtitle">
              {loading
                ? "Loading testers from App Store Connect…"
                : (
                  <>
                    Showing <strong style={{ color: "var(--text-1)" }}>{filteredTesters.length}</strong>
                    {" "}of <strong style={{ color: "var(--text-1)" }}>{testers.length}</strong> testers
                  </>
                )}
            </div>
          </div>
          <div className="page-header__right">
            <button
              className="ov-quick-btn"
              onClick={() => setRefresh((r) => !r)}
              title="Refresh from App Store Connect"
            >
              <i className="bi bi-arrow-clockwise" /> Refresh
            </button>
            <button
              className="ov-quick-btn ov-quick-btn--accent"
              onClick={openInviteModal}
              disabled={groups.length === 0}
              title={groups.length === 0
                ? "No beta groups available — create one in App Store Connect first"
                : "Invite a new tester"}
            >
              <i className="bi bi-plus-lg" /> Invite Tester
            </button>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-card__head">
              <div className="stat-card__label">Total Testers</div>
              <div className="stat-card__icon stat-card__icon--blue">
                <i className="bi bi-people-fill" />
              </div>
            </div>
            <div className="stat-card__value">{counts.all}</div>
            <div className="stat-card__delta stat-card__delta--info">
              <i className="bi bi-apple" /> Across all beta groups
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card__head">
              <div className="stat-card__label">Invited</div>
              <div className="stat-card__icon stat-card__icon--amber">
                <i className="bi bi-envelope-paper" />
              </div>
            </div>
            <div className="stat-card__value">{counts.invited}</div>
            <div className="stat-card__delta stat-card__delta--down">
              <i className="bi bi-hourglass-split" /> Awaiting acceptance
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card__head">
              <div className="stat-card__label">Accepted</div>
              <div className="stat-card__icon stat-card__icon--violet">
                <i className="bi bi-person-check-fill" />
              </div>
            </div>
            <div className="stat-card__value">{counts.accepted}</div>
            <div className="stat-card__delta stat-card__delta--up">
              <i className="bi bi-check2-circle" /> Onboarded to TestFlight
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card__head">
              <div className="stat-card__label">Installed</div>
              <div className="stat-card__icon stat-card__icon--green">
                <i className="bi bi-phone-vibrate-fill" />
              </div>
            </div>
            <div className="stat-card__value">{counts.installed}</div>
            <div className="stat-card__delta stat-card__delta--up">
              <i className="bi bi-graph-up" /> Active beta installs
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="dt-toolbar">
          <div className="dt-toolbar__left">
            <div className="dt-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  className={`dt-filter-pill ${filter === f.id ? "dt-filter-pill--active" : ""}`}
                  onClick={() => setFilter(f.id)}
                >
                  <i className={`bi ${f.icon}`} />
                  {f.label}
                  <span className="dt-filter-pill__count">{counts[f.id] || 0}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="dt-toolbar__right">
            <div className="dt-search">
              <i className="bi bi-search dt-search__icon" />
              <input
                type="text"
                placeholder="Search name or email…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Testers Table ── */}
        <div className="dt-shell">
          {loading ? (
            <div className="dt-loading">
              <div className="spinner-border" />
              <span>Loading testers…</span>
            </div>
          ) : loadError ? (
            <div className="dt-empty">
              <div className="dt-empty__icon"><i className="bi bi-exclamation-triangle" /></div>
              <h4>Could not load testers</h4>
              <p>{loadError}</p>
              <button className="ov-quick-btn" onClick={() => setRefresh((r) => !r)}>
                <i className="bi bi-arrow-clockwise" /> Try again
              </button>
            </div>
          ) : filteredTesters.length === 0 ? (
            <div className="dt-empty">
              <div className="dt-empty__icon"><i className="bi bi-person-plus" /></div>
              <h4>{testers.length === 0 ? "No testers yet" : "No testers match this filter"}</h4>
              <p>
                {testers.length === 0
                  ? "Invite your first iOS beta tester to get started."
                  : "Try a different status or clear the search box."}
              </p>
              {testers.length === 0 && groups.length > 0 && (
                <button className="ov-quick-btn ov-quick-btn--accent" onClick={openInviteModal}>
                  <i className="bi bi-plus-lg" /> Invite Tester
                </button>
              )}
            </div>
          ) : (
            <table className="dt-table">
              <thead>
                <tr>
                  <th>Tester</th>
                  <th>Status</th>
                  <th>Groups</th>
                  <th>Invitation Sent</th>
                  <th className="dt-th--right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTesters.map((t) => {
                  const status = stateMeta(t.state);
                  const fullName = `${t.firstName || ""} ${t.lastName || ""}`.trim() || "—";
                  const busy = rowBusy[t.id];
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="dt-user">
                          <div className={`dt-avatar ${avatarColor(t.email || fullName)}`}>
                            {initialsOf(t)}
                          </div>
                          <div className="dt-cell-stack">
                            <div className="dt-name">{fullName}</div>
                            <div className="dt-sub">{t.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`dt-pill ${status.cls}`}>{status.label}</span>
                      </td>
                      <td>
                        {t.betaGroups?.length ? (
                          <div className="tf-group-chips">
                            {t.betaGroups.map((g) => (
                              <span key={g.id} className="dt-pill dt-pill--soft">
                                {g.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: "var(--text-3)" }}>—</span>
                        )}
                      </td>
                      <td>
                        {t.invitedAt ? (
                          <div className="dt-cell-stack">
                            <div
                              className="dt-name"
                              style={{ fontSize: "0.82rem", fontWeight: 500 }}
                              title={formatTimestamp(t.invitedAt)}
                            >
                              {formatTimestamp(t.invitedAt)}
                            </div>
                            <div className="dt-sub" style={{ fontSize: "0.72rem" }}>
                              {relativeTime(t.invitedAt)}
                              {t.lastResendAt && (
                                <>
                                  {" · "}
                                  <span
                                    title={`Last resent ${formatTimestamp(t.lastResendAt)}`}
                                    style={{ color: "var(--amber)" }}
                                  >
                                    <i className="bi bi-arrow-repeat" /> {relativeTime(t.lastResendAt)}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "var(--text-3)" }}>—</span>
                        )}
                      </td>
                      <td className="dt-td--right">
                        <div className="ps-row-actions">
                          <button
                            className="dt-action-btn dt-action-btn--accent"
                            title="Resend invitation email"
                            disabled={!!busy}
                            onClick={() => setConfirmResend(t)}
                          >
                            {busy === "resending" ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <i className="bi bi-envelope-arrow-up" />
                            )}
                          </button>
                          <button
                            className="dt-action-btn"
                            title="Remove tester"
                            disabled={!!busy}
                            onClick={() => setConfirmRemove(t)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Invite Modal ── */}
      <Modal show={showInvite} onHide={() => setShowInvite(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-apple me-2" />
            Invite TestFlight Tester
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitInvite}>
          <Modal.Body>
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>
              Apple will email the invitation directly to the tester. Pick the beta group they should
              join — they will see the linked builds as soon as they accept.
            </p>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>First name</Form.Label>
                <Form.Control
                  type="text"
                  value={inviteForm.firstName}
                  onChange={(e) =>
                    setInviteForm((f) => ({ ...f, firstName: e.target.value }))
                  }
                  placeholder="Optional"
                />
              </Form.Group>
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Last name</Form.Label>
                <Form.Control
                  type="text"
                  value={inviteForm.lastName}
                  onChange={(e) =>
                    setInviteForm((f) => ({ ...f, lastName: e.target.value }))
                  }
                  placeholder="Optional"
                />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                required
                value={inviteForm.email}
                onChange={(e) =>
                  setInviteForm((f) => ({ ...f, email: e.target.value }))
                }
                placeholder="tester@example.com"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beta group</Form.Label>
              <Form.Select
                required
                value={inviteForm.groupId}
                onChange={(e) =>
                  setInviteForm((f) => ({ ...f, groupId: e.target.value }))
                }
              >
                <option value="" disabled>Choose a group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}{g.isInternalGroup ? " (internal)" : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {inviteError && (
              <div className="tf-form-error">
                <i className="bi bi-exclamation-circle me-2" />
                {inviteError}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowInvite(false)}
              disabled={inviteSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={inviteSubmitting}>
              {inviteSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Sending…
                </>
              ) : (
                <>Send Invitation</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Resend Confirmation Modal ── */}
      <Modal
        show={!!confirmResend}
        onHide={() => !resendSubmitting && setConfirmResend(null)}
        size="sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-envelope-arrow-up me-2" />
            Resend invitation?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">
            Apple will email a fresh TestFlight invitation to{" "}
            <strong>{confirmResend?.email}</strong>.
          </p>
          {confirmResend?.invitedAt && (
            <p className="text-muted mb-1" style={{ fontSize: "0.82rem" }}>
              Originally invited {relativeTime(confirmResend.invitedAt)}
              {confirmResend.lastResendAt && (
                <> · last resent {relativeTime(confirmResend.lastResendAt)}</>
              )}
              .
            </p>
          )}
          <p className="text-muted" style={{ fontSize: "0.82rem" }}>
            Avoid spamming — Apple may throttle repeated invitations.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmResend(null)}
            disabled={resendSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmResend}
            disabled={resendSubmitting}
          >
            {resendSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Sending…
              </>
            ) : (
              <>Resend</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Remove Confirmation Modal ── */}
      <Modal
        show={!!confirmRemove}
        onHide={() => !removeSubmitting && setConfirmRemove(null)}
        size="sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove tester?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">
            This removes <strong>{confirmRemove?.email}</strong> from the app's TestFlight testers in
            App Store Connect.
          </p>
          <p className="text-muted" style={{ fontSize: "0.82rem" }}>
            The tester will lose access to all linked beta builds. You can invite them again later.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmRemove(null)}
            disabled={removeSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmRemove}
            disabled={removeSubmitting}
          >
            {removeSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Removing…
              </>
            ) : (
              <>Remove</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default TestFlightScreen;
